import { List } from 'immutable'
import _get from 'lodash/get'
import FingerprintJS from '@fingerprintjs/fingerprintjs-pro'
import {
  getGoogleRecaptchaScript,
  getCaptchaVerifyToken,
  CAPTCHA_ACTION_BILLING_PAYMENT_FORM_VIEW,
} from 'services/captcha'
import { fromCallback as fromCallbackPromise } from 'bluebird'
import { get as getConfig } from 'config'
import {
  setPaymentMethodsProcessing,
  setPaymentMethods,
  setPaymentDataProcessing,
  setPaymentData,
  setPaymentCaptchaValid,
  setPaymentError,
  resetPaymentError,
  setPaymentAuthorization,
} from 'services/payment/actions'
import {
  PAYMENT_ERROR_TYPE_UNKNOWN,
  PAYMENT_ERROR_GIFT_PAYMENT_PENDING,
} from 'services/payment'
import {
  checkVelocity,
  setAuthorization,
  getVelocityCheckActionLocation,
} from 'services/velocity-check'
import { getUserAccountValidation } from 'services/user-account'
import {
  SET_VELOCITY_CHECK_PROCESSING,
  SET_VELOCITY_CHECK_DATA,
  SET_VELOCITY_AUTHORIZATION,
} from 'services/velocity-check/actions'
import { US } from 'services/country'
import { USD } from 'services/currency'
import { getPrimary } from 'services/languages'
import { addNewRelicAction } from 'services/app/_newrelic'
import { PLAN_ID_ONE_WEEK_FREE_TRIAL_TO_MONTHLY } from 'services/plans' // adyen-test-page
import {
  isTestAdyenPage,
  getBaseNewRelicData,
  adyenMerchantAccount,
  getAdyenPaymentMethods,
  allowedPaymentMethods,
  getAdyenAuthorization,
  updateAdyenAuthorization,
  getAdyenPayments,
  getAdyenPaymentDetails,
  handleGetAdyenPayments,
  handleGetAdyenPaymentMethods,
  getPaypalOrder,
  formatBillingAddress,
  ADYEN_PAYMENT_RESPONSE_CODE_AUTHORIZED,
  ADYEN_PAYMENT_RESPONSE_CODE_PENDING,
  ADYEN_PAYMENT_RESPONSE_CODE_RECEIVED,
  ADYEN_PAYMENT_TYPE_CARD,
  ADYEN_PAYMENT_TYPE_PAYPAL,
  ADYEN_PAYMENT_TYPE_APPLEPAY,
  ADYEN_FIELD_DEFAULT_VALUE,
} from '.'
import * as actions from './actions'

const config = getConfig()
const fingerprintJsApiKey = _get(config, 'fingerPrintJsApiKey')
const fingerprintJsSubdomain = _get(config, 'fingerPrintJsSubdomain')
const adyenPaypalActive = !!_get(config, ['features', 'checkout', 'adyenPaypal'])
const velocityCheckActive = !!_get(config, ['features', 'checkout', 'velocityCheck'])
const newRelicAdyenPaymentMonitorName = 'AdyenPayment'
const newRelicAdyenPaymentIframeMonitorName = 'AdyenPaymentIframeError'
const giftingPendingRefusalReason = 'Gift payment pending'

async function getPaymentMethods (options) {
  const {
    auth,
    userUuid,
    country,
    primaryLang,
    currencyIso,
  } = options

  const paymentMethods = await getAdyenPaymentMethods({
    auth,
    shopperReference: userUuid,
    merchantAccount: adyenMerchantAccount,
    countryCode: country,
    shopperLocale: primaryLang,
    allowedPaymentMethods,
    amount: { currency: currencyIso, value: 0 },
  })

  return paymentMethods
}

async function getFingerprint () {
  // get fingerprint
  const fpPromise = FingerprintJS.load({
    apiKey: fingerprintJsApiKey,
    endpoint: fingerprintJsSubdomain,
  })
  const fp = await fpPromise
  const fpResult = await fp.get()

  return fpResult
}

async function getFingerprintProtectedPaymentMethods (options) {
  const {
    state,
    dispatch,
  } = options
  const {
    auth,
    user,
    plans,
    checkout,
    resolver,
    userAccount,
  } = state
  const userInfoUuid = user.getIn(['data', 'uuid'])
  let velocityPage = getVelocityCheckActionLocation({ auth, checkout, resolver })
  const plansList = plans.getIn(['data', 'plans'], List())
  const plansCountry = plansList.size > 0 ? plansList.first().get('country') : undefined
  const plansCurrency = plansList.size > 0 ? plansList.first().get('currencyIso') : undefined
  const authCountry = auth.get('country')
  const userAccountCountry = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'countryIso'])
  const userAccountCurrency = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'currencyIso'])
  const country = userAccountCountry || authCountry || plansCountry || US
  const currencyIso = userAccountCurrency || plansCurrency || USD
  const primaryLang = getPrimary(user.getIn(['data', 'language'], List()))
  const userUuid = user.getIn(['data', 'uuid'])

  // adyen-test-page
  if (isTestAdyenPage({ state })) {
    velocityPage = 'checkout'
  }

  dispatch({
    type: SET_VELOCITY_CHECK_PROCESSING,
    payload: true,
  })

  const fpResult = await getFingerprint()
  const velocity = await checkVelocity({ userInfoUuid, fingerprintId: _get(fpResult, 'visitorId'), page: velocityPage })
  const velocitySuccess = _get(velocity, 'success')

  dispatch({
    type: SET_VELOCITY_CHECK_DATA,
    payload: { data: velocity, processing: false },
  })

  if (velocitySuccess) {
    const adyenPaymentMethods = await getPaymentMethods({
      auth,
      userUuid,
      country,
      primaryLang,
      currencyIso,
    })
    dispatch(setPaymentMethods(adyenPaymentMethods))
  }
}

function setPaymentTokenFailed (options) {
  const {
    auth,
    dropin,
    dispatch,
    refusalReason,
    initialResultCode,
    paymentType,
    authorizationUuid,
    giftPaymentPending,
    velocityCheckToken,
    baseNewRelicData,
  } = options
  const authTokenNewRelicFailureData = {
    paymentType,
    paymentTokenRefusalReason: refusalReason,
    paymentTokenResultCode: initialResultCode,
    paymentTokenSuccess: false,
  }

  // set discarded since there was an error so the worker can clean up the table properly
  updateAdyenAuthorization({ uuid: authorizationUuid, auth, discarded: true })
  dispatch(setPaymentData(handleGetAdyenPayments({}, true)))

  if (giftPaymentPending) {
    dispatch(setPaymentError(PAYMENT_ERROR_GIFT_PAYMENT_PENDING))
  }

  if (velocityCheckActive && velocityCheckToken) {
    dispatch({
      type: SET_VELOCITY_AUTHORIZATION,
      payload: { success: false, reason: refusalReason },
    })
  }

  // send failure data to New Relic
  addNewRelicAction(
    newRelicAdyenPaymentMonitorName,
    { ...baseNewRelicData, ...authTokenNewRelicFailureData },
  )

  // reset the iframe status
  dropin.setStatus('ready')
}

/**
 * Watcher for initializing the Adyen Drop In.
 *
 * @param {Object} param0 The parameters
 * @param {Function} param0.after The witness after function
 */
export function watchInitAdyen ({ after }) {
  return after(actions.INIT_ADYEN, async ({ state, dispatch }) => {
    const {
      user,
      auth,
      plans,
      userAccount,
    } = state
    const { googleRecaptchaSiteKey } = getConfig()
    const plansList = plans.getIn(['data', 'plans'], List())
    const plansCountry = plansList.size > 0 ? plansList.first().get('country') : undefined
    const plansCurrency = plansList.size > 0 ? plansList.first().get('currencyIso') : undefined
    const authCountry = auth.get('country')
    const userAccountCountry = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'countryIso'])
    const userAccountCurrency = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'currencyIso'])
    const country = userAccountCountry || authCountry || plansCountry || US
    const currencyIso = userAccountCurrency || plansCurrency || USD
    const primaryLang = getPrimary(user.getIn(['data', 'language'], List()))
    const userUuid = user.getIn(['data', 'uuid'])

    dispatch(setPaymentMethodsProcessing(true))

    await getGoogleRecaptchaScript()
    const { grecaptcha } = global

    if (grecaptcha) {
      await fromCallbackPromise((cb) => grecaptcha.ready(() => cb()))
      const token = await grecaptcha.execute(googleRecaptchaSiteKey, {
        action: CAPTCHA_ACTION_BILLING_PAYMENT_FORM_VIEW,
      })
      const tokenResponse = await getCaptchaVerifyToken({ token })
      const { valid } = tokenResponse

      if (valid) {
        if (velocityCheckActive) {
          await getFingerprintProtectedPaymentMethods({ state, dispatch })
        } else {
          // if velocity check is not turned on, allow the user through
          const adyenPaymentMethods = await getPaymentMethods({
            auth,
            userUuid,
            country,
            primaryLang,
            currencyIso,
          })
          dispatch(setPaymentMethods(adyenPaymentMethods))
        }
      } else {
        dispatch(setPaymentMethodsProcessing(false))
      }

      dispatch(setPaymentCaptchaValid(valid))
    } else {
      // if the Google Recaptcha script fails to load

      /* eslint-disable no-lonely-if */
      if (velocityCheckActive) {
        await getFingerprintProtectedPaymentMethods({ state, dispatch })
      } else {
        // just fail if velocity check is not enabled and recaptcha fails to load
        dispatch(setPaymentMethods(handleGetAdyenPaymentMethods({}, true)))
      }
      /* eslint-enable no-lonely-if */
    }
  })
}

/**
 * Watcher for getting payment authorization from Adyen.
 *
 * @param {Object} param0 The parameters
 * @param {Function} param0.after The witness after function
 */
export function watchGetAdyenPaymentAuthorization ({ after }) {
  return after(
    actions.GET_ADYEN_PAYMENT_AUTHORIZATION,
    async ({ state, action, dispatch }) => {
      const {
        user,
        auth,
        plans,
        payment,
        userAccount,
        velocityCheck,
        checkout,
        resolver,
      } = state
      const { payload } = action
      const {
        billingAddress,
        paymentMethod,
        isGift,
        dropin,
      } = payload
      const shopperReference = user.getIn(['data', 'uuid'])
      const velocityCheckToken = velocityCheck.getIn(['data', 'velocityCheck', 'token'])
      const currencyIso = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'currencyIso'])
        || plans.getIn(['selection', 'currencyIso'])
        || USD
      const paymentType = _get(paymentMethod, 'type')
      const cardPaymentType = paymentType === ADYEN_PAYMENT_TYPE_CARD
      const applepayPaymentType = paymentType === ADYEN_PAYMENT_TYPE_APPLEPAY
      const baseNewRelicData = getBaseNewRelicData(state)
      const testResultCode = payment.getIn(['data', 'testResultCode'])
      const productRatePlanId = isTestAdyenPage({ state }) ? PLAN_ID_ONE_WEEK_FREE_TRIAL_TO_MONTHLY : plans.getIn(['selection', 'id'])
      const billingAccountId = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'billingAccountId'])
      let velocityPage = getVelocityCheckActionLocation({ auth, checkout, resolver })

      // kick off the payment process
      dispatch(setPaymentDataProcessing(true))
      dispatch(resetPaymentError())

      // if the payment type is card and
      // if the user doesn't have a Gaia billing account,
      // check the postal code
      if (cardPaymentType && !isGift && !billingAccountId) {
        // validate the postal code
        const accountValidation = await getUserAccountValidation({
          country: _get(billingAddress, 'country'),
          postalCode: _get(billingAddress, 'postalCode'),
          productRatePlanId,
          auth,
        })
        const accountValidationSuccess = _get(accountValidation, 'success')
        const accountValidationErrorCode = _get(accountValidation, ['data', 'errorCode']) || PAYMENT_ERROR_TYPE_UNKNOWN

        // if the postal code is not valid,
        // we will bail and not continue with submitting the payment
        if (!accountValidationSuccess) {
          if (velocityCheckActive) {
            const userInfoUuid = user.getIn(['data', 'uuid'])

            // adyen-test-page
            if (isTestAdyenPage({ state })) {
              velocityPage = 'checkout'
            }

            // since the postal code failed, call the velocity check again
            dispatch({
              type: SET_VELOCITY_CHECK_PROCESSING,
              payload: true,
            })

            const fpResult = await getFingerprint()
            const velocity = await checkVelocity({ userInfoUuid, fingerprintId: _get(fpResult, 'visitorId'), page: velocityPage })

            dispatch({
              type: SET_VELOCITY_CHECK_DATA,
              payload: { data: velocity, processing: false },
            })
          }

          dispatch(setPaymentError(accountValidationErrorCode))

          // reset the iframe status
          dropin.setStatus('ready')

          return
        }
      }

      // get authorization, which provides the reference/order number
      const authorization = await getAdyenAuthorization({
        auth,
        shopperReference,
        velocityCheckUuid: velocityCheckToken,
        pageName: velocityPage,
      })
      const reference = _get(authorization, 'reference')
      const authorizationUuid = _get(authorization, 'uuid')

      if (_get(authorization, 'success') && reference) {
        // for non-card payments, set the authorization
        if (!cardPaymentType) {
          dispatch(setPaymentAuthorization(authorization))
        }

        // get payments data
        const payments = await getAdyenPayments({
          auth,
          shopperReference: user.getIn(['data', 'uuid']),
          shopperIP: user.getIn(['data', 'xff']),
          shopperEmail: user.getIn(['data', 'mail']),
          merchantAccount: adyenMerchantAccount,
          amount: { currency: currencyIso, value: 0 },
          paymentMethod,
          reference,
          testResultCode,
        })
        const paymentAction = _get(payments, ['data', 'action'])

        // handle any payment action that may exist
        if (paymentAction) {
          dropin.handleAction(paymentAction)
        }

        const pspReference = _get(payments, ['data', 'pspReference'])
        const initialResultCode = _get(payments, ['data', 'resultCode'])
        const recurringDetailReference = _get(payments, ['data', 'additionalData', 'recurring.recurringDetailReference'])
        const authorized = initialResultCode === ADYEN_PAYMENT_RESPONSE_CODE_AUTHORIZED
        const pendingOrReceived = initialResultCode === ADYEN_PAYMENT_RESPONSE_CODE_PENDING
          || initialResultCode === ADYEN_PAYMENT_RESPONSE_CODE_RECEIVED
        const authorizedOrPending = authorized || pendingOrReceived
        const giftPaymentPending = isGift && cardPaymentType && pendingOrReceived
        const extraData = {
          billingAddress,
          authorization,
        }

        if (!_get(payments, 'success') || _get(payments, 'error') || !authorizedOrPending || giftPaymentPending) {
          const refusalReason = giftPaymentPending ? giftingPendingRefusalReason : _get(payments, ['data', 'refusalReason'])
          setPaymentTokenFailed({
            auth,
            dropin,
            dispatch,
            refusalReason,
            initialResultCode,
            paymentType,
            authorizationUuid,
            giftPaymentPending,
            velocityCheckToken,
            baseNewRelicData,
          })

          return
        }

        // if this is a credit or debit card, set payment data and update authorization
        if (cardPaymentType || applepayPaymentType) {
          dispatch(setPaymentData({ ...payments, ...extraData }))
          // close the loop on authorization, setting payment authorization data
          updateAdyenAuthorization({
            uuid: authorizationUuid,
            auth,
            pspReference,
            initialResultCode,
            recurringDetailReference,
          })

          // send authorization data for the velocity check to protect CC abuse
          // but don't send for Pending state
          if (
            authorized
            && velocityCheckActive
            && velocityCheckToken
          ) {
            dispatch({
              type: SET_VELOCITY_AUTHORIZATION,
              payload: { success: true, reason: null },
            })
          }

          // send success data to New Relic
          const authTokenNewRelicSuccessData = {
            paymentType,
            paymentTokenResultCode: initialResultCode,
            paymentTokenSuccess: true,
          }

          addNewRelicAction(
            newRelicAdyenPaymentMonitorName,
            { ...baseNewRelicData, ...authTokenNewRelicSuccessData },
          )
        }
      } else {
        dispatch(setPaymentData(handleGetAdyenPayments({}, true)))
      }
    },
  )
}

/**
 * Watcher for getting payment authorization details from Adyen.
 *
 * @param {Object} param0 The parameters
 * @param {Function} param0.after The witness after function
 */
export function watchGetAdyenPaymentAuthorizationDetails ({ after }) {
  return after(
    [actions.GET_ADYEN_PAYMENT_AUTHORIZATION_DETAILS],
    async ({ dispatch, state, action }) => {
      const { auth, velocityCheck, payment } = state
      const { payload } = action
      const { data, dropin, isGift } = payload
      const velocityCheckToken = velocityCheck.getIn(['data', 'velocityCheck', 'token'])
      const authorizationUuid = payment.getIn(['data', 'payments', 'authorization', 'uuid'])
      const details = await getAdyenPaymentDetails({ data, auth })
      const paymentType = _get(details, ['data', 'paymentMethod', 'type'])
      const baseNewRelicData = getBaseNewRelicData(state)
      const pspReference = _get(details, ['data', 'pspReference'])
      const initialResultCode = _get(details, ['data', 'resultCode'])
      const recurringDetailReference = _get(details, ['data', 'additionalData', 'recurring.recurringDetailReference'])
      const authorized = initialResultCode === ADYEN_PAYMENT_RESPONSE_CODE_AUTHORIZED
      const pendingOrReceived = initialResultCode === ADYEN_PAYMENT_RESPONSE_CODE_PENDING
        || initialResultCode === ADYEN_PAYMENT_RESPONSE_CODE_RECEIVED
      const authorizedOrPending = authorized || pendingOrReceived
      const giftPaymentPending = isGift && pendingOrReceived
      const orderId = _get(data, ['details', 'orderID'])
      let extraData = {}

      if (!_get(details, 'success') || _get(details, 'error') || !authorizedOrPending || giftPaymentPending) {
        const refusalReason = giftPaymentPending ? giftingPendingRefusalReason : _get(details, ['data', 'refusalReason'])
        setPaymentTokenFailed({
          auth,
          dropin,
          dispatch,
          refusalReason,
          initialResultCode,
          paymentType,
          authorizationUuid,
          giftPaymentPending,
          velocityCheckToken,
          baseNewRelicData,
        })

        return
      }

      if (paymentType === ADYEN_PAYMENT_TYPE_PAYPAL && orderId) {
        const paypalOrder = await getPaypalOrder({ auth, orderId })
        const paypalAddress = _get(paypalOrder, ['data', 'payment_source', 'paypal', 'address'], {})
        const paypalPostalCode = _get(paypalAddress, 'postal_code', '')
        const paypalCountry = _get(paypalAddress, 'country_code', '')

        if (_get(paypalOrder, 'success') && paypalPostalCode && paypalCountry) {
          const billingAddress = formatBillingAddress({
            address1: _get(paypalAddress, 'address_line_1', ''),
            address2: _get(paypalAddress, 'address_line_2', ''),
            city: _get(paypalAddress, 'admin_area_2', ''),
            state: _get(paypalAddress, 'admin_area_1', ''),
            postalCode: paypalPostalCode,
            country: paypalCountry,
          })
          extraData = {
            billingAddress,
            authorization: payment.getIn(['data', 'payments', 'authorization']),
          }
        } else {
          // if the paypal order api call fails, provide some defaults
          // so we don't ruin the checkout process over an address
          const billingAddress = formatBillingAddress({
            address1: ADYEN_FIELD_DEFAULT_VALUE,
            address2: ADYEN_FIELD_DEFAULT_VALUE,
            city: ADYEN_FIELD_DEFAULT_VALUE,
            state: ADYEN_FIELD_DEFAULT_VALUE,
            postalCode: '80027',
            country: US,
          })
          extraData = {
            billingAddress,
            authorization: payment.getIn(['data', 'payments', 'authorization']),
          }
        }
      }

      dispatch(setPaymentData({ ...details, ...extraData }))

      // close the loop on authorization, setting payment authorization data
      updateAdyenAuthorization({
        uuid: authorizationUuid,
        auth,
        pspReference,
        initialResultCode,
        recurringDetailReference,
      })

      // set the dropin to ready since everything happened successfully
      dropin.setStatus('ready')

      // send authorization data for the velocity check to protect CC abuse
      // but don't send for Pending state
      if (
        authorized
        && velocityCheckActive
        && velocityCheckToken
      ) {
        dispatch({
          type: SET_VELOCITY_AUTHORIZATION,
          payload: { success: true, reason: null },
        })
      }

      // send success data to New Relic
      const authTokenNewRelicSuccessData = {
        paymentType,
        paymentTokenResultCode: initialResultCode,
        paymentTokenSuccess: true,
      }

      addNewRelicAction(
        newRelicAdyenPaymentMonitorName,
        { ...baseNewRelicData, ...authTokenNewRelicSuccessData },
      )
    },
  )
    .when(({ state }) => {
      return adyenPaypalActive || isTestAdyenPage({ state }) // adyen-test-page
    })
}

export function watchSetVelocityAuthorization ({ after }) {
  return after([SET_VELOCITY_AUTHORIZATION], async ({ dispatch, state, action }) => {
    const {
      user, checkout, auth, resolver, velocityCheck,
    } = state
    const { payload } = action
    const { success, reason } = payload
    const userUuid = user.getIn(['data', 'uuid'])
    const velocityCheckToken = velocityCheck.getIn(['data', 'velocityCheck', 'token'])
    let velocityPage = getVelocityCheckActionLocation({ auth, checkout, resolver })
    const fpResult = await getFingerprint()
    const fingerprintId = _get(fpResult, 'visitorId')

    // adyen-test-page
    if (isTestAdyenPage({ state })) {
      velocityPage = 'checkout'
    }

    await setAuthorization({
      success,
      fingerprintId,
      userInfoUuid: userUuid,
      token: velocityCheckToken,
      failureReason: reason,
    })

    if (!success) {
      // if the payment token is not successful, then get a new set of velocity data
      dispatch({
        type: SET_VELOCITY_CHECK_PROCESSING,
        payload: true,
      })

      const velocity = await checkVelocity(
        {
          userInfoUuid: userUuid,
          fingerprintId,
          page: velocityPage,
        },
      )

      dispatch({
        type: SET_VELOCITY_CHECK_DATA,
        payload: { data: velocity, processing: false },
      })
    } else {
      dispatch({
        type: SET_VELOCITY_CHECK_PROCESSING,
        payload: false,
      })
    }
  })
    .when(() => {
      return velocityCheckActive
    })
}

export function watchSetAdyenDropInError ({ after }) {
  return after([actions.SET_ADYEN_DROP_IN_ERROR], async ({ dispatch, state, action }) => {
    const {
      user,
      checkout,
      payment,
      auth,
      resolver,
      velocityCheck,
    } = state
    const { payload } = action
    const userUuid = user.getIn(['data', 'uuid'])
    const velocityCheckToken = velocityCheck.getIn(['data', 'velocityCheck', 'token'])
    const authorizationUuid = payment.getIn(['data', 'payments', 'authorization', 'uuid'])
    let velocityPage = getVelocityCheckActionLocation({ auth, checkout, resolver })
    const baseNewRelicData = getBaseNewRelicData(state)
    const adyenErrorData = {
      iframeError: payload,
    }

    // adyen-test-page
    if (isTestAdyenPage({ state })) {
      velocityPage = 'checkout'
    }

    // remove processing for payments
    dispatch(setPaymentDataProcessing(false))

    // if there is an authorization uuid
    // set discarded since there was an error so the worker can clean up the table properly
    if (authorizationUuid) {
      updateAdyenAuthorization({ uuid: authorizationUuid, auth, discarded: true })
    }

    if (velocityCheckActive && velocityCheckToken) {
      dispatch({
        type: SET_VELOCITY_CHECK_PROCESSING,
        payload: true,
      })

      const fpResult = await getFingerprint()
      const fingerprintId = _get(fpResult, 'visitorId')

      // set velocity authorization
      await setAuthorization({
        success: false,
        fingerprintId,
        userInfoUuid: userUuid,
        token: velocityCheckToken,
        failureReason: payload,
      })
      // fetch another set of velocity data
      const velocity = await checkVelocity(
        {
          userInfoUuid: userUuid,
          fingerprintId,
          page: velocityPage,
        },
      )

      dispatch({
        type: SET_VELOCITY_CHECK_DATA,
        payload: { data: velocity, processing: false },
      })
    }

    // send iframe error data to New Relic
    addNewRelicAction(
      newRelicAdyenPaymentIframeMonitorName,
      { ...baseNewRelicData, ...adyenErrorData },
    )
  })
}
